package com.partejar.app.feature.contratos.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.contratos.data.*
import com.partejar.app.feature.relacionamentos.data.RelacionamentosDataSource
import com.partejar.app.feature.relacionamentos.data.RelationshipEdit
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface ContratoUiState {
    data object Loading : ContratoUiState
    data class SetupRequired(val requirements: ContractSetupRequirements) : ContratoUiState

    data class Content(
        val contract: Contrato,
        val options: ContratoOptions,
        val civil: CivilData,
        val patient: com.partejar.app.feature.relacionamentos.data.RelationshipDetail,
        val preparation: ContractPreparationProgress,
        val signatures: ContractSignatures? = null,
        val link: SignatureLink? = null,
        val pdf: ContractPdf? = null,
        val submitting: Boolean = false,
        val actionError: String? = null
    ) : ContratoUiState

    data class Error(val message: String) : ContratoUiState
}

enum class ContratoCompletedAction { CONFIGURATION, CIVIL_DATA }

class ContratoViewModel private constructor(
    private val source: ContratosDataSource,
    private val relationships: RelacionamentosDataSource,
    private val scopeOverride: CoroutineScope?,
    @Suppress("UNUSED_PARAMETER") marker: Unit
) : ViewModel() {
    constructor(source: ContratosDataSource, relationships: RelacionamentosDataSource) :
        this(source, relationships, null, Unit)

    internal constructor(
        source: ContratosDataSource,
        relationships: RelacionamentosDataSource,
        scope: CoroutineScope
    ) : this(source, relationships, scope, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _state = MutableStateFlow<ContratoUiState>(ContratoUiState.Loading)
    val state = _state.asStateFlow()
    private val _completedAction = MutableSharedFlow<ContratoCompletedAction>(extraBufferCapacity = 1)
    val completedAction = _completedAction.asSharedFlow()

    /** Runs in the screen's lifecycle scope, so backgrounding/navigation cancels requests. */
    suspend fun observePendingSignatures() {
        state.map { state ->
            (state as? ContratoUiState.Content)?.contract?.takeIf {
                it.status == "PRONTO_PARA_ASSINATURA" || it.status == "AGUARDANDO_ASSINATURA_PACIENTE" ||
                    it.status == "AGUARDANDO_ASSINATURAS_EQUIPE"
            }?.id
        }.distinctUntilChanged().collectLatest { id ->
            if (id == null) return@collectLatest
            var interval = 15_000L
            while (currentCoroutineContext().isActive) {
                interval = if (refreshSignatures(id)) 15_000L else (interval * 2).coerceAtMost(60_000L)
                delay(interval)
            }
        }
    }

    private suspend fun refreshSignatures(id: Long): Boolean {
        val current = _state.value as? ContratoUiState.Content ?: return true
        if (current.contract.id != id || current.submitting) return true
        return try {
            val contract = source.get(id)
            val signatures = source.signatures(id)
            // Separate reads can straddle a signature. Wait for a coherent response.
            if (contract.status != signatures.status) return false
            // An action or reload may have changed the state while these reads were in flight.
            if (_state.value === current) {
                _state.value = current.copy(
                    contract = contract,
                    signatures = signatures,
                    link = current.link.takeIf { contract.status == "AGUARDANDO_ASSINATURA_PACIENTE" }
                )
            }
            true
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            // Connectivity failures must not infer cancellation, expiration, or completion.
            false
        }
    }

    private var loadJob: Job? = null
    fun loadOrStart(patientId: Long): Job {
        loadJob?.cancel()
        return scope.launch {
            _state.value = ContratoUiState.Loading
            _state.value = try {
                val options = source.options()
                currentCoroutineContext().ensureActive()
                var patient = relationships.detail(patientId)
                currentCoroutineContext().ensureActive()
                val initialRequirements = options.setupRequirements()
                if (patient.stage != com.partejar.app.feature.relacionamentos.data.RelacionamentoStage.CONTRACT && initialRequirements.isMissing) {
                    _state.value = ContratoUiState.SetupRequired(initialRequirements)
                    return@launch
                }
                val contract = source.start(patientId)
                currentCoroutineContext().ensureActive()
                if (patient.stage != com.partejar.app.feature.relacionamentos.data.RelacionamentoStage.CONTRACT) {
                    patient = relationships.detail(patientId)
                    currentCoroutineContext().ensureActive()
                }
                val requirements = options.setupRequirements(contract)
                if (!contract.isFrozen && requirements.isMissing) {
                    _state.value = ContratoUiState.SetupRequired(requirements)
                    return@launch
                }
                val civil = source.civil(contract.id)
                val signatures = if (contract.isFrozen || contract.content != null) source.signatures(contract.id) else null
                currentCoroutineContext().ensureActive()
                ContratoUiState.Content(
                    contract = contract,
                    options = options,
                    civil = civil,
                    patient = patient,
                    preparation = contractPreparationProgress(contract, civil, patient),
                    signatures = signatures
                )
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                currentCoroutineContext().ensureActive()
                ContratoUiState.Error("Não foi possível abrir o contrato.")
            }
        }.also { loadJob = it }
    }

    fun updatePregnancy(babyName: String, dueDate: String, maternity: String) = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        if (!contractPrerequisitesReady(babyName, dueDate)) {
            _state.value = current.copy(actionError = "Informe o nome do bebê e uma DPP válida para continuar.")
            return@launch
        }
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            val patient = relationships.update(
                current.patient.id,
                current.patient,
                RelationshipEdit(
                    name = current.patient.name,
                    babyName = babyName.trim(),
                    maternity = maternity.trim().ifBlank { null },
                    dueDate = dueDate,
                    livesInBh = current.patient.livesInBh
                )
            )
            _state.value = current.copy(
                contract = source.get(current.contract.id),
                patient = patient,
                preparation = current.preparation.complete(ContractPreparationStep.PREGNANCY),
                submitting = false,
                actionError = null
            )
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível atualizar os dados da gestação.")
        }
    }

    fun configure(planId: Long, modelId: Long, signerIds: List<Long>) = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            val contract = source.configure(
                current.contract.id,
                ContractConfiguration(current.contract.patientId, planId, modelId, signerIds)
            )
            _state.value = current.copy(
                contract = contract,
                preparation = current.preparation.complete(ContractPreparationStep.CONTRACT),
                submitting = false,
                actionError = null
            )
            _completedAction.emit(ContratoCompletedAction.CONFIGURATION)
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível salvar o contrato.")
        }
    }

    fun updateCivil(cpf: String, email: String) = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            val civil = source.updateCivil(current.contract.id, cpf, email)
            _state.value = current.copy(
                contract = source.get(current.contract.id),
                civil = civil,
                preparation = current.preparation.complete(ContractPreparationStep.PATIENT),
                submitting = false,
                actionError = null
            )
            _completedAction.emit(ContratoCompletedAction.CIVIL_DATA)
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível atualizar os dados civis.")
        }
    }

    fun generate() = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        if (!contractPrerequisitesReady(current.patient.babyName, current.patient.dueDate)) {
            _state.value = current.copy(actionError = "Informe o nome do bebê e a DPP antes de gerar o contrato.")
            return@launch
        }
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            val contract = source.generate(current.contract.id)
            _state.value = current.copy(
                contract = contract,
                civil = source.civil(current.contract.id),
                preparation = current.preparation.finish(),
                submitting = false,
                actionError = null
            )
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível salvar o contrato.")
        }
    }

    fun cancel() = submit { current -> source.cancel(current.contract.id) }

    fun generateSignatureLink() = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        if (current.submitting) return@launch
        _state.value = current.copy(submitting = true, actionError = null)
        var issuedLink: SignatureLink? = null
        try {
            val link = source.createSignatureLink(current.contract.id)
            issuedLink = link
            // Creating a new token invalidates the old one even if the subsequent reads fail.
            _state.value = current.copy(link = link, submitting = true, actionError = null)
            _state.value = current.copy(
                contract = source.get(current.contract.id),
                signatures = source.signatures(current.contract.id),
                link = link,
                submitting = false
            )
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(
                link = issuedLink ?: current.link,
                submitting = false,
                actionError = if (issuedLink == null) "Não foi possível gerar o link de assinatura."
                    else "Link gerado. Não foi possível atualizar as assinaturas agora."
            )
        }
    }

    fun signAsTeam(signatureStrokes: String) = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        val documentHash = current.contract.documentHash
        if (documentHash.isNullOrBlank()) {
            _state.value = current.copy(actionError = "O documento congelado não possui hash para assinatura.")
            return@launch
        }
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            val signatures = source.signAsTeam(current.contract.id, documentHash, signatureStrokes)
            _state.value = current.copy(
                contract = source.get(current.contract.id),
                signatures = signatures,
                submitting = false
            )
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível registrar sua assinatura.")
        }
    }

    fun openPdf() = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        if (current.contract.content == null) {
            _state.value = current.copy(actionError = "O documento deste contrato ainda não está disponível.")
            return@launch
        }
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            _state.value = current.copy(pdf = source.pdf(current.contract.id), submitting = false)
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível abrir o PDF do contrato.")
        }
    }

    fun pdfHandled(error: String?) {
        val current = _state.value as? ContratoUiState.Content ?: return
        _state.value = current.copy(pdf = null, actionError = error)
    }

    private fun submit(operation: suspend (ContratoUiState.Content) -> Contrato) = scope.launch {
        val current = _state.value as? ContratoUiState.Content ?: return@launch
        _state.value = current.copy(submitting = true, actionError = null)
        try {
            _state.value = current.copy(
                contract = operation(current),
                civil = source.civil(current.contract.id),
                submitting = false
            )
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = current.copy(submitting = false, actionError = "Não foi possível salvar o contrato.")
        }
    }
}
